import type { FamilyShellComputer } from "@crew/ui/components/family-shell";
import type { Me } from "../../app/me";
import { defaultUnserved } from "../setup/onboarding";

/** The owner can retry a computer whose setup failed. */
export function canRetryComputer(me: Pick<Me, "computer" | "user">): boolean {
  return me.computer?.status === "error" && me.user.role === "owner";
}

/**
 * The home page links to Settings → AI when a set-up computer's AI isn't connected
 * (slice 1b spec §6.3): it needs reconnecting, or a connect failed.
 */
export function needsAiSettings(computer: Me["computer"]): boolean {
  if (!computer || aiView(computer.ai) === null) return false;
  // An update has nothing for the owner to do there (slice 1j §6.4).
  if (computer.ai.updating) return false;
  return !["setting_up", "waiting", "released", "error"].includes(computer.status);
}

/**
 * The sidebar's computer card: only while the computer needs something
 * (setting up, waking, waiting, an error, the AI). Awake or asleep shows as
 * the Chief of Staff's presence dot instead (`computerAwake`).
 */
export function sidebarComputer(computer: Me["computer"]): FamilyShellComputer | null {
  const quiet = ["ready", "asleep", "sleeping"].includes(computer?.status ?? "");
  if (quiet && computer && aiView(computer.ai) === null) return null;
  return computerView(computer);
}

/** The computer is up: the Chief of Staff's presence dot. */
export function computerAwake(computer: Me["computer"]): boolean {
  return computer?.status === "ready";
}

/**
 * How the computer reads: the sidebar well (`tone`, `title`, `detail`) and
 * the Chats card's one line. `ready` reads as Awake, `asleep` and `sleeping` as Asleep.
 */
export function computerView(computer: Me["computer"]): FamilyShellComputer & { line: string } {
  switch (computer?.status) {
    case "setting_up":
      return {
        tone: "working",
        title: "Setting up your computer",
        detail: "A few minutes the first time.",
        line: "Setting up your computer…",
      };
    case "waiting":
      return {
        tone: "queued",
        title: "Waiting for a computer",
        detail: "Your asks will queue.",
        line: "Waiting for a computer. Your asks will queue.",
      };
    case "waking":
      // An update wakes the computer first: it reads as the update (Devin on #405).
      if (computer.ai.updating) return aiView(computer.ai)!;
      return {
        tone: "working",
        title: "Waking your computer",
        detail: "Back in a moment.",
        line: "Waking your computer…",
      };
    case "error":
      return {
        tone: "error",
        title: "Computer needs attention",
        detail: "Setting it up didn't finish.",
        line: computer.error ?? "Setting up your computer didn't finish.",
      };
    case "released":
      return {
        tone: "asleep",
        title: "Computer released",
        detail: "Resubscribe to get it back.",
        line: "Computer released",
      };
    case undefined:
      return {
        tone: "asleep",
        title: "No computer yet",
        detail: "Finish checkout to get one.",
        line: "No computer yet",
      };
    // Awake or not is what the person can use: an awake computer answers at
    // once, a sleeping one takes about a minute to wake first.
    case "ready":
      return (
        aiView(computer.ai) ?? {
          tone: "done",
          title: "Computer awake",
          detail: "Answers right away.",
          line: "Awake",
        }
      );
    default:
      return (
        (computer && aiView(computer.ai)) ?? {
          tone: "asleep",
          title: "Computer asleep",
          detail: "Wakes in about a minute.",
          line: "Asleep",
        }
      );
  }
}

/**
 * A set-up computer whose AI isn't connected reads as that (slice 1b spec
 * §6.2, §6.3): nothing can be asked until it is. Null when it's connected.
 */
function aiView(
  ai: NonNullable<Me["computer"]>["ai"],
): (FamilyShellComputer & { line: string }) | null {
  // A connect never moves the default (slice 1c spec §3.4): the owner chooses one.
  if (defaultUnserved(ai)) {
    return {
      tone: "error",
      title: "Choose a default model",
      detail: "In Settings → AI.",
      line: "No connected account serves your default model. Choose a default in Settings → AI.",
    };
  }
  // Slice 1j §6.3: an update takes asks; they queue until it's done.
  if (ai.updating) {
    return {
      tone: "working",
      title: "Updating",
      detail: "Messages are queued until your crew workspace is ready.",
      line: "Messages are queued until your crew workspace is ready.",
    };
  }
  switch (ai.status) {
    case "connected":
      return null;
    case "connecting":
      return {
        tone: "working",
        title: "Connecting your AI",
        detail: "About a minute.",
        line: "Connecting your AI…",
      };
    case "needs_reconnect":
      return {
        tone: "error",
        title: "AI needs reconnecting",
        detail: "Reconnect in Settings.",
        line: ai.error ?? "Your AI connection stopped working. Reconnect it in Settings.",
      };
    default:
      // It has worked before (slice 1j §6.1a): a lost update, say.
      if (ai.connected_at) {
        return {
          tone: "error",
          title: "Not connected",
          detail: "Messages can't be sent until your crew workspace is connected again.",
          line: "Messages can't be sent until your crew workspace is connected again.",
        };
      }
      return {
        tone: "waiting",
        title: "Connect your AI",
        detail: "Nothing can be asked until then.",
        line: "Connect your AI to start.",
      };
  }
}
